import { useLocation, useNavigate } from '@solidjs/router'
import { type Component, Show, createEffect, createSignal, on, onMount } from 'solid-js'
import { isApiError } from '../../api/errors'
import { HMPendingApprovalPage } from '../../features/auth-shared/HMPendingApprovalPage'
import { I18nProvider } from '../../i18n'
import { authStore } from '../../stores/auth.store'
import { ToastProvider } from '../../stores/toast.store'
import { workspaceStore } from '../../stores/workspace.store'
import { loginPathFor, storeReturnPath } from '../utils/authRoutes'
import { RouteSkeleton } from './RouteSkeleton'

const AWAITING_APPROVAL_CODES = new Set(['account_pending', 'account_rejected'])

const isAwaitingApproval = (err: unknown) =>
  isApiError(err) && err.status === 403 && !!err.code && AWAITING_APPROVAL_CODES.has(err.code)

export const HiringManagerRedirect: Component = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [sessionKnown, setSessionKnown] = createSignal(false)
  const [refusedForApproval, setRefusedForApproval] = createSignal(false)

  const isHiringManager = () =>
    authStore.state.isAuthenticated && authStore.state.userType === 'hiring_manager'
  const canOpenWorkspace = () => sessionKnown() && isHiringManager() && authStore.isHMApproved
  const showPendingScreen = () =>
    sessionKnown() && isHiringManager() && (!authStore.isHMApproved || refusedForApproval())

  const openWorkspace = async () => {
    setRefusedForApproval(false)
    try {
      if (workspaceStore.workspaces.length === 0) {
        await workspaceStore.fetchWorkspaces()
      }
    } catch (err) {
      if (isAwaitingApproval(err)) {
        setRefusedForApproval(true)
        void authStore.revalidate()
        return
      }
      throw err
    }
    const slug = workspaceStore.defaultWorkspaceSlug
    if (!slug) {
      navigate('/app/w/create', { replace: true })
      return
    }
    let subpath = location.pathname.replace(/^\/app\/hiring-manager\/?/, '') || 'dashboard'
    let search = location.search
    if (subpath === 'integrations') {
      subpath = 'workspace'
      search = search ? `${search}&tab=integrations` : '?tab=integrations'
    }
    navigate(`/app/w/${slug}/${subpath}${search}`, { replace: true })
  }

  onMount(async () => {
    if (authStore.state.isAuthenticated) {
      authStore.completeBootstrap()
    } else {
      await authStore.checkAuth()
    }
    if (!isHiringManager()) {
      storeReturnPath(location.pathname + location.search)
      navigate(loginPathFor('hiring_manager'), { replace: true })
      return
    }
    setSessionKnown(true)
  })

  createEffect(
    on(canOpenWorkspace, (ready) => {
      if (ready) {
        void openWorkspace()
      }
    })
  )

  createEffect(
    on(
      () => sessionKnown() && !isHiringManager(),
      (signedOut) => {
        if (signedOut) {
          navigate(loginPathFor('hiring_manager'), { replace: true })
        }
      }
    )
  )

  return (
    <Show when={showPendingScreen()} fallback={<RouteSkeleton />}>
      <I18nProvider>
        <ToastProvider>
          <HMPendingApprovalPage />
        </ToastProvider>
      </I18nProvider>
    </Show>
  )
}
